// src/pages/Tavern.tsx
import React, { useState, useEffect } from 'react'
import api from '../api/axios'
import './Tavern.css'
import buttonImg    from '../assets/tavern/button.png'
import commonImg    from '../assets/tavern/common.png'
import rareImg      from '../assets/tavern/rare.png'
import legendaryImg from '../assets/tavern/legendary.png'

type LootboxStatus = { keys: number; common: number; rare: number; legendary: number }
type Reward        = { gold?: number; silver?: number; xp?: number }
type BJStart       = { result: 'ongoing'|'blackjack'|'push'; deck?:string[]; playerHand?:string[]; dealerHand?:(string|null)[]; bet?:number; payout?:number; silver:number }
type BJAction      = { result:'continue'|'bust'|'win'|'lose'|'push'; deck?:string[]; playerHand?:string[]; dealerHand?:string[]; outcome?:string; value?:{player:number;dealer:number}; silver:number }

// Lootbox slideshow data
const BOX_SLIDES = [
  { type: 'common'    as const, img: commonImg,    label: 'Common'    },
  { type: 'rare'      as const, img: rareImg,      label: 'Rare'      },
  { type: 'legendary' as const, img: legendaryImg, label: 'Legendary' },
]

export default function Tavern() {
  // tab state
  const [activeTab, setActiveTab] = useState<'lootbox'|'blackjack'>('lootbox')

  // Lootbox state
  const [lbStatus, setLbStatus]   = useState<LootboxStatus|null>(null)
  const [opening, setOpening]     = useState<string|null>(null)
  const [slideIdx, setSlideIdx]   = useState(0)

  // Blackjack state
  const [bet, setBet]             = useState(0)
  const [bj, setBj]               = useState<BJStart|null>(null)
  const [bjLoading, setBjLoading] = useState(false)

  useEffect(fetchLootbox, [])

  function fetchLootbox() {
    api.get<LootboxStatus>('/lootbox')
      .then(r => setLbStatus(r.data))
      .catch(err => alert(err.response?.data?.error || err.message))
  }

  function openCurrent() {
    const type = BOX_SLIDES[slideIdx].type
    setOpening(type)
    api.post<{ reward: Reward }>('/lootbox/open',{ type })
      .then(r => {
        const parts = []
        if (r.data.reward.gold)   parts.push(`${r.data.reward.gold} gold`)
        if (r.data.reward.silver) parts.push(`${r.data.reward.silver} silver`)
        if (r.data.reward.xp)     parts.push(`${r.data.reward.xp} XP`)
        alert(`You got: ${parts.join(', ')}`)
        fetchLootbox()
      })
      .catch(err=>alert(err.response?.data?.error||err.message))
      .finally(()=>setOpening(null))
  }

  function prevBox() { setSlideIdx(i=> i===0?BOX_SLIDES.length-1:i-1) }
  function nextBox() { setSlideIdx(i=> i===BOX_SLIDES.length-1?0:i+1) }

  function startBJ() {
    setBjLoading(true)
    api.post<BJStart>('/casino/blackjack',{ bet })
      .then(r => {
        const d=r.data
        if (d.result==='blackjack'||d.result==='push') {
          alert(d.result==='blackjack'
            ? `Blackjack! You win ${d.payout} silver!`
            : 'Push! Your bet is returned.')
          setBj(null)
        } else setBj(d)
      })
      .catch(err=>alert(err.response?.data?.error||err.message))
      .finally(()=>setBjLoading(false))
  }

  function hit() {
    if (!bj) return
    setBjLoading(true)
    api.post<BJAction>('/casino/blackjack/hit',{
      deck: bj.deck, playerHand: bj.playerHand, dealerHand: bj.dealerHand, bet: bj.bet
    })
      .then(r=>{
        const d=r.data
        if (d.result==='continue') setBj({ ...bj, deck:d.deck, playerHand:d.playerHand, dealerHand:d.dealerHand })
        else {
          alert(d.result==='bust'
            ? 'Busted!'
            : `Result: ${d.result.toUpperCase()}. You: ${d.value?.player}, Dealer: ${d.value?.dealer}`)
          setBj(null)
        }
      })
      .catch(err=>alert(err.response?.data?.error||err.message))
      .finally(()=>setBjLoading(false))
  }

  function stand() {
    if (!bj) return
    setBjLoading(true)
    api.post<BJAction>('/casino/blackjack/stand',{
      deck: bj.deck, playerHand: bj.playerHand, dealerHand: bj.dealerHand, bet: bj.bet
    })
      .then(r=>{
        const d=r.data
        alert(`Result: ${d.result.toUpperCase()}. You: ${d.value?.player}, Dealer: ${d.value?.dealer}`)
        setBj(null)
      })
      .catch(err=>alert(err.response?.data?.error||err.message))
      .finally(()=>setBjLoading(false))
  }

  return (
    <div className="tavern-page">
      <div className="tavern-container">
        <div className="tab-bar">
          {(['lootbox','blackjack'] as const).map(tab => (
            <button
              key={tab}
              className={`tab-btn${activeTab===tab?' active':''}`}
              onClick={()=>setActiveTab(tab)}
              style={{ backgroundImage:`url(${buttonImg})` }}
            >
              {tab==='lootbox'? 'Lootboxes' : 'Blackjack'}
            </button>
          ))}
        </div>

        {activeTab==='lootbox' ? (
          <section className="lootbox-slideshow">
            {lbStatus ? (
              <>
                <button className="slide-btn prev" onClick={prevBox} />
                <img
                  src={BOX_SLIDES[slideIdx].img}
                  alt={BOX_SLIDES[slideIdx].label}
                  className="box-img"
                />
                <button className="slide-btn next" onClick={nextBox} />
                <div className="box-caption">{BOX_SLIDES[slideIdx].label}</div>

                <div className="box-details">
                  <p>Keys: {lbStatus.keys}</p>
                  <p>Available: {(lbStatus as any)[BOX_SLIDES[slideIdx].type]}</p>
                </div>

                <button
                  className="tavern-btn open-btn"
                  style={{ backgroundImage:`url(${buttonImg})` }}
                  onClick={openCurrent}
                  disabled={opening===BOX_SLIDES[slideIdx].type}
                >
                  {opening===BOX_SLIDES[slideIdx].type
                    ? 'Opening…'
                    : `Open ${BOX_SLIDES[slideIdx].label}`}
                </button>
              </>
            ) : (
              <p className="loading">Loading lootboxes…</p>
            )}
          </section>
        ) : (
          <section className="bj-section">
            <h3>Blackjack</h3>
            {bj ? (
              <div className="bj-table">
                <p>You: {bj.playerHand!.join(' ')}</p>
                <p>Dealer: {bj.dealerHand!.map(c=>c||'❓').join(' ')}</p>
                <div className="bj-buttons">
                  <button className="tavern-btn" onClick={hit}   disabled={bjLoading}>Hit</button>
                  <button className="tavern-btn" onClick={stand} disabled={bjLoading}>Stand</button>
                </div>
              </div>
            ) : (
              <div className="bj-start">
                <input
                  type="number"
                  className="bet-input"
                  value={bet}
                  onChange={e=>setBet(+e.target.value)}
                  placeholder="Bet amount"
                />
                <button
                  className="tavern-btn"
                  onClick={startBJ}
                  disabled={bjLoading||bet<=0}
                >
                  {bjLoading ? '…' : 'Play'}
                </button>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  )
}
